import { useQuery } from '@tanstack/react-query';
import { Receipt } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate } from '@/lib/utils';
import { clientDetailApi, type InvoiceSummaryItem } from '@/api/client-detail';

const formatMoney = (amount: number) =>
  amount.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function cardBg(item: InvoiceSummaryItem): string {
  if (item.paymentState === 'PAID')
    return 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-900';
  if (item.overdueDays !== null && item.overdueDays > 40)
    return 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-900';
  if (item.overdueDays !== null && item.overdueDays > 20)
    return 'bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-900';
  return 'bg-muted/50 border-border';
}

export function InvoiceSummarySection({ clientId }: { clientId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['client-invoice-summary', clientId],
    queryFn: () => clientDetailApi.getInvoiceSummary(clientId),
  });

  if (isLoading) {
    return (
      <div className="border-t pt-4 space-y-2">
        <h3 className="font-medium flex items-center gap-2">
          <Receipt className="h-4 w-4" />
          Сводка счетов
        </h3>
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="border-t pt-4 space-y-2">
        <h3 className="font-medium flex items-center gap-2">
          <Receipt className="h-4 w-4" />
          Сводка счетов
        </h3>
        <p className="text-sm text-muted-foreground">Нет счетов</p>
      </div>
    );
  }

  return (
    <div className="border-t pt-4 space-y-2">
      <h3 className="font-medium flex items-center gap-2">
        <Receipt className="h-4 w-4" />
        Сводка счетов
      </h3>
      <div className="space-y-2">
        {data.map((item) => (
          <div key={item.id} className={`rounded-lg border p-3 space-y-1.5 ${cardBg(item)}`}>
            {/* Header: number, date, status */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">#{item.number}</span>
                <span className="text-xs text-muted-foreground">{formatDate(item.date)}</span>
              </div>
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                item.paymentState === 'PAID'
                  ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                  : 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'
              }`}>
                {item.paymentState === 'PAID' ? 'Оплачен' : 'Не оплачен'}
              </span>
            </div>

            {/* Amount + overdue */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">{formatMoney(item.amount)} BYN</span>
              {item.overdueDays !== null && (
                <span className="text-xs text-red-600 dark:text-red-400">
                  {item.overdueDays} дн. просрочки
                </span>
              )}
            </div>

            {/* Details row */}
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
              <span>ПК: {item.pcCount} ({formatMoney(item.pricePerPc)})</span>
              <span>Серв.: {item.serverCount} ({formatMoney(item.pricePerServer)})</span>
              {item.employee && <span>Обсл.: {item.employee.lastName}</span>}
              {item.hasServices && <span>Доп.: {formatMoney(item.servicesTotal)}</span>}
            </div>

            {/* Offices */}
            {item.offices.length > 0 && (
              <div className="text-xs text-muted-foreground">
                {item.offices.map((o, i) => (
                  <span key={i}>
                    {o.name}{o.employeeLastName ? ` (${o.employeeLastName})` : ''}: {o.accountedPCs} КК
                    {i < item.offices.length - 1 ? ' · ' : ''}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
